import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useIsAdmin, useUserRole } from "@/hooks/useUserRole";
import { useEffect, useState } from "react";

export type SectionKey = string;

export type SubSection = { key: string; label: string; paths: string[] };
export type SectionDef = { key: string; label: string; description: string; children?: SubSection[] };

export const SECTIONS: SectionDef[] = [
  { key: "insights", label: "Analytics & Executive", description: "Analytics and Executive Dashboard" },
  { key: "sales", label: "Sales", description: "Sales portal, pipeline and leads" },
  { key: "marketing", label: "Marketing", description: "Marketing portal and campaigns" },
  { key: "finance", label: "Finance", description: "Cash flow and invoicing" },
  { key: "research", label: "R&D", description: "Research & development" },
  {
    key: "operations", label: "Operations / Bookings", description: "Choose which operations areas this user sees",
    children: [
      { key: "ops_coffeebeans", label: "Coffeebeans", paths: ["/operations/coffeebeans", "/operations/availability", "/availability-management"] },
      { key: "ops_snapstack", label: "SnapStack", paths: ["/operations/snapstack"] },
      { key: "ops_weekend_specials", label: "Weekend Specials", paths: ["/operations/weekend-specials", "/weekend-specials"] },
      { key: "ops_creative_travel", label: "Creative Travel", paths: ["/operations/creative-travel"] },
    ],
  },
  {
    key: "company_admin", label: "Company Admin", description: "Choose which company records this user sees",
    children: [
      { key: "ca_registration", label: "Company Registration", paths: ["/portal/company-admin/company-registration"] },
      { key: "ca_fair_trade", label: "FAIR TRADE", paths: ["/portal/company-admin/fair-trade"] },
      { key: "ca_hr", label: "HR", paths: ["/portal/company-admin/hr"] },
      { key: "ca_insurance", label: "Insurance", paths: ["/portal/company-admin/insurance"] },
      { key: "ca_legal", label: "Legal", paths: ["/portal/company-admin/legal"] },
      { key: "ca_our_rates", label: "Our Rates", paths: ["/portal/company-admin/our-rates"] },
      { key: "ca_supplier_rates", label: "Supplier Rates", paths: ["/portal/company-admin/supplier-rates"] },
      { key: "ca_sars", label: "SARS", paths: ["/portal/company-admin/sars"] },
    ],
  },
  { key: "database", label: "Database", description: "Clients, suppliers, products, destinations" },
  { key: "projects", label: "Projects & Issues", description: "Projects and issue tracker" },
];

/** Every grantable key (sub-sections replace their parent group). */
export const ALL_LEAF_KEYS: string[] = SECTIONS.flatMap((s) => (s.children ? s.children.map((c) => c.key) : [s.key]));

const parentOf = (key: string) => SECTIONS.find((s) => s.children?.some((c) => c.key === key));

/** Expand legacy parent grants (e.g. "operations") into all their sub-sections. */
export const normalizeSections = (rows: string[]): string[] => {
  const out = new Set<string>();
  rows.forEach((r) => {
    const def = SECTIONS.find((s) => s.key === r);
    if (def?.children) def.children.forEach((c) => out.add(c.key));
    else out.add(r);
  });
  return [...out];
};

/** Map a URL path to the section/sub-section that governs it (null = always allowed). */
export const sectionForPath = (path: string): SectionKey | null => {
  for (const s of SECTIONS) for (const c of s.children ?? []) if (c.paths.some((p) => path.startsWith(p))) return c.key;
  if (path.startsWith("/analytics") || path.startsWith("/executive")) return "insights";
  if (path.startsWith("/department/sales") || path.startsWith("/pipeline") || path.startsWith("/lead/")) return "sales";
  if (path.startsWith("/department/marketing") || path.startsWith("/promo-codes")) return "marketing";
  if (path.startsWith("/department/finance")) return "finance";
  if (path.startsWith("/department/research")) return "research";
  if (path.startsWith("/operations") || path.startsWith("/department/operations")) return "operations";
  if (path.startsWith("/portal/company-admin")) return "company_admin";
  if (["/clients", "/suppliers", "/products", "/destinations"].some((p) => path.startsWith(p))) return "database";
  if (path.startsWith("/projects") || path.startsWith("/project/") || path.startsWith("/issues")) return "projects";
  return null;
};

const ADMIN_ONLY = ["/users", "/admin", "/brand-settings", "/project-setup", "/payment-webhook"];
export const isAdminOnlyPath = (path: string) => ADMIN_ONLY.some((p) => path === p || path.startsWith(p + "/") || path.startsWith(p + "-"));

export const useSectionAccess = () => {
  const [userId, setUserId] = useState<string | null>(null);
  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => setUserId(data.session?.user?.id ?? null));
  }, []);
  const { isAdmin, isLoading: adminLoading } = useIsAdmin();
  const { isLoading: rolesLoading } = useUserRole();

  const { data: sections, isLoading } = useQuery({
    queryKey: ["section-access", userId],
    enabled: !!userId,
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const { data, error } = await supabase.from("user_section_access").select("section").eq("user_id", userId!);
      if (error) throw error;
      return normalizeSections((data ?? []).map((r) => r.section));
    },
  });

  const restricted = !isAdmin && (sections?.length ?? 0) > 0;
  const can = (section: SectionKey | null) => {
    if (!section || !restricted) return true;
    const def = SECTIONS.find((s) => s.key === section);
    if (def?.children) return def.children.some((c) => sections!.includes(c.key));
    return sections!.includes(section) || false;
  };
  const canPath = (path: string) => (isAdminOnlyPath(path) ? isAdmin : can(sectionForPath(path)));

  return { can, canPath, isAdmin, restricted, isLoading: !userId || isLoading || adminLoading || rolesLoading };
};

export { parentOf };
